import React, { useState, useEffect, useCallback } from "react";
import {
  PlusCircle,
  Trash2,
  Edit2,
  X,
  Image as ImageIcon,
  Calendar,
  User,
  Upload,
  Tag,
  Link as LinkIcon,
  Star,
} from "lucide-react";
import marathiService from "../../services/marathiService";

// Custom Alert Component
const Alert = ({ children, variant, className = "" }) => {
  const baseStyles = "flex w-full rounded-lg border p-4";
  const variantStyles =
    variant === "destructive"
      ? "border-red-200 bg-red-50 text-red-600"
      : "border-gray-200 bg-gray-50 text-gray-600";

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = "" }) => (
  <div className={`text-sm ${className}`}>{children}</div>
);

const MarathiBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    thumbnail: "",
    content: [""],
    AnyLink: "",
    date: new Date().toISOString().split("T")[0],
    author: "Admin",
    tags: [],
    images: [],
    featured: false,
    language: "Marathi",
  });

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      // FIXED: Changed from marathiService.marathiBlogs.blogs to marathiService.blogs
      const response = await marathiService.blogs.getAll({});

      // Handle different response structures
      let blogsData;
      if (Array.isArray(response)) {
        blogsData = response;
      } else if (response.data && Array.isArray(response.data)) {
        blogsData = response.data;
      } else if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        blogsData = response.data.data;
      } else if (response.blogs && Array.isArray(response.blogs)) {
        // Handle Marathi API specific format
        blogsData = response.blogs;
      } else {
        console.error("Unexpected response structure:", response);
        setError("Invalid data format");
        blogsData = [];
      }

      setBlogs(blogsData);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError(err.message || "Failed to fetch blogs");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;

    if (name === "content") {
      const newContent = [...formData.content];
      newContent[index] = value;
      setFormData((prev) => ({ ...prev, content: newContent }));
    } else if (name === "tags") {
      const tagsArray = value
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
      setFormData((prev) => ({ ...prev, tags: tagsArray }));
    } else if (name === "AnyLink") {
      setFormData((prev) => ({ ...prev, AnyLink: value }));
    } else if (name === "featured") {
      setFormData((prev) => ({ ...prev, featured: e.target.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddParagraph = () => {
    setFormData((prev) => ({
      ...prev,
      content: [...prev.content, ""],
    }));
  };

  const handleRemoveParagraph = (index) => {
    setFormData((prev) => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e, type) => {
    if (type === "thumbnail" && typeof e === "string") {
      // Direct base64 string case
      setFormData((prev) => ({ ...prev, thumbnail: e }));
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError(
        `${
          type === "thumbnail" ? "Thumbnail" : "Image"
        } size should be less than 5MB`
      );
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "thumbnail") {
        setFormData((prev) => ({ ...prev, thumbnail: reader.result }));
      } else if (type === "image") {
        if (formData.images.length >= 5) {
          setError("Maximum 5 images are allowed");
          return;
        }
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedImage = canvas.toDataURL("image/jpeg", 0.7);
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, compressedImage],
          }));
        };
        img.src = reader.result;
      }
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    setError("");

    if (!formData.title.trim()) {
      setError("Title is required");
      return false;
    }

    if (!formData.thumbnail) {
      setError("Thumbnail is required");
      return false;
    }

    if (!formData.content.some((paragraph) => paragraph.trim())) {
      setError("At least one content paragraph is required");
      return false;
    }

    if (formData.images && formData.images.length > 5) {
      setError("Maximum 5 images are allowed");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError("");

      const formDataToSend = {
        title: formData.title.trim(),
        thumbnail: formData.thumbnail,
        content: formData.content.filter(
          (paragraph) => paragraph.trim() !== ""
        ),
        AnyLink: formData.AnyLink || "",
        date: new Date(formData.date).toISOString(),
        author: formData.author.trim() || "Admin",
        tags: formData.tags.filter((tag) => tag.trim() !== ""),
        images: formData.images || [],
        featured: formData.featured || false,
        language: "Marathi",
      };

      // FIXED: Changed from marathiService.marathiBlogs.blogs to marathiService.blogs
      if (selectedBlog) {
        await marathiService.blogs.update(selectedBlog._id, formDataToSend);
        setSuccess("Blog updated successfully!");
      } else {
        await marathiService.blogs.create(formDataToSend);
        setSuccess("Blog created successfully!");
      }

      await fetchBlogs();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error saving blog:", error);
      setError(error.message || "Failed to save blog. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (blog) => {
    setSelectedBlog(blog);
    setFormData({
      title: blog.title || "",
      thumbnail: blog.thumbnail || "",
      content: blog.content || [""],
      AnyLink: blog.AnyLink || "",
      date: blog.date
        ? new Date(blog.date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      author: blog.author || "Admin",
      tags: blog.tags || [],
      images: blog.images || [],
      featured: blog.featured || false,
      language: "Marathi",
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      // FIXED: Changed from marathiService.marathiBlogs.blogs to marathiService.blogs
      await marathiService.blogs.delete(selectedBlog._id);
      setSuccess("Blog deleted successfully!");
      fetchBlogs();
      setShowDeleteModal(false);
      setSelectedBlog(null);
    } catch (error) {
      console.error("Error deleting blog:", error);
      setError(error.message || "Failed to delete blog");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      thumbnail: "",
      content: [""],
      AnyLink: "",
      date: new Date().toISOString().split("T")[0],
      author: "Admin",
      tags: [],
      images: [],
      featured: false,
      language: "Marathi",
    });
    setSelectedBlog(null);
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Marathi Blogs Management
        </h1>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <PlusCircle className="mr-2" size={20} />
            Add Blog
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Card Grid Layout */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      ) : blogs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">
            No blogs found. Create one to get started!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog) => (
            <div
              key={blog._id}
              className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200"
            >
              {/* Thumbnail Section */}
              <div className="h-48 overflow-hidden">
                <img
                  src={blog.thumbnail || "/placeholder.jpg"}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content Section */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold text-gray-800">
                    {blog.title}
                  </h3>
                  {blog.featured && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs">
                      <Star className="mr-1" size={14} />
                      Featured
                    </span>
                  )}
                </div>

                {/* Blog Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-gray-600">
                    <User size={16} className="mr-2" />
                    {blog.author || "Admin"}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar size={16} className="mr-2" />
                    {new Date(blog.date).toLocaleDateString()}
                  </div>
                  {blog.tags && blog.tags.length > 0 && (
                    <div className="flex items-center text-gray-600">
                      <Tag size={16} className="mr-2" />
                      <div className="flex flex-wrap gap-1">
                        {blog.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Content Preview */}
                <div className="mb-4">
                  <p className="text-gray-600 line-clamp-3">
                    {blog.content?.[0] || "No content"}
                  </p>
                </div>

                {/* Action Links */}
                <div className="flex justify-between items-center">
                  {blog.AnyLink ? (
                    <a
                      href={blog.AnyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-red-500 hover:text-red-600"
                    >
                      <LinkIcon size={16} className="mr-1" />
                      View Link
                    </a>
                  ) : (
                    <div></div>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(blog)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedBlog(blog);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedBlog ? "Edit Blog" : "Add Blog"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                {/* Author */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1 space-y-2">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">
                          Upload thumbnail
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, "thumbnail")}
                          required={!formData.thumbnail}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Content Paragraphs */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">
                      Content *
                    </label>
                    <button
                      type="button"
                      onClick={handleAddParagraph}
                      className="text-red-500 hover:text-red-600"
                    >
                      + Add Paragraph
                    </button>
                  </div>
                  {formData.content.map((paragraph, index) => (
                    <div key={index} className="relative">
                      <textarea
                        name="content"
                        value={paragraph}
                        onChange={(e) => handleInputChange(e, index)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[100px]"
                        required
                      />
                      {formData.content.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveParagraph(index)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-600"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Any Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Any Link
                  </label>
                  <input
                    type="text"
                    name="AnyLink"
                    value={formData.AnyLink}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags.join(", ")}
                    onChange={handleInputChange}
                    placeholder="Enter tags separated by commas"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                {/* Additional Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images (Max 5)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index),
                            }));
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-500">
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, "image")}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : selectedBlog
                      ? "Update Blog"
                      : "Create Blog"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Delete Blog
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedBlog?.title}"? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !showModal && !showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarathiBlogs;
